// ================================
// HOLOGRAPHIC CALL INTERFACE
// JavaScript Functionality
// ================================

import '../styles/hologram.css';
import * as THREE from 'three';
import { gsap } from 'gsap';

// ================================
// STATE MANAGEMENT
// ================================
const state = {
  callActive: true,
  participants: [],
  features: {
    spatial: true,
    translate: false,
    emotion: true,
    background: false
  },
  controls: {
    mic: true,
    camera: true,
    hand: false,
    share: false,
    record: false
  },
  callStartTime: Date.now(),
  timerInterval: null
};

// ================================
// THREE.JS HOLOGRAM SCENE
// ================================
function initHologramScene() {
  const canvas = document.getElementById('hologram-canvas');
  if (!canvas) return;
  
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
  
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  
  camera.position.z = 8;
  camera.position.y = 2;
  
  // Create participant hologram ring
  const participants = [
    { emoji: '👨‍💼', name: 'Alex Chen', position: 0, color: 0x00f3ff },
    { emoji: '👩‍💻', name: 'Sarah Johnson', position: 1, color: 0xff00ff },
    { emoji: '👨‍🔬', name: 'Dr. Patel', position: 2, color: 0x8000ff },
    { emoji: '👩‍🎨', name: 'Maya Rodriguez', position: 3, color: 0x00ff88 },
    { emoji: '👨‍🚀', name: 'Commander Kim', position: 4, color: 0xff6600 },
    { emoji: '👩‍🏫', name: 'Prof. Anderson', position: 5, color: 0xffff00 },
    { emoji: '👨‍⚕️', name: 'Dr. Williams', position: 6, color: 0xff0080 }
  ];
  
  state.participants = participants;
  
  const hologramGroup = new THREE.Group();
  const ringRadius = 5;
  const angleStep = (Math.PI * 2) / participants.length;
  
  participants.forEach((participant, index) => {
    const angle = angleStep * index;
    const x = Math.cos(angle) * ringRadius;
    const z = Math.sin(angle) * ringRadius;
    
    // Create hologram platform
    const platformGeometry = new THREE.CylinderGeometry(0.8, 0.8, 0.1, 32);
    const platformMaterial = new THREE.MeshPhongMaterial({
      color: participant.color,
      emissive: participant.color,
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.6,
      wireframe: false
    });
    const platform = new THREE.Mesh(platformGeometry, platformMaterial);
    platform.position.set(x, 0, z);
    
    // Create hologram beam
    const beamGeometry = new THREE.CylinderGeometry(0.7, 0.7, 3, 32, 1, true);
    const beamMaterial = new THREE.MeshBasicMaterial({
      color: participant.color,
      transparent: true,
      opacity: 0.2,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    });
    const beam = new THREE.Mesh(beamGeometry, beamMaterial);
    beam.position.set(x, 1.5, z);
    
    // Create hologram rings around participant
    const rings = [];
    for (let i = 0; i < 3; i++) {
      const ringGeometry = new THREE.TorusGeometry(0.5 + i * 0.2, 0.02, 16, 100);
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: participant.color,
        transparent: true,
        opacity: 0.4,
        blending: THREE.AdditiveBlending
      });
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.position.set(x, 1 + i * 0.5, z);
      ring.rotation.x = Math.PI / 2;
      rings.push(ring);
      hologramGroup.add(ring);
    }
    
    hologramGroup.add(platform);
    hologramGroup.add(beam);
    
    // Store reference
    participant.mesh = { platform, beam, rings };
  });
  
  scene.add(hologramGroup);
  
  // Add ambient light
  const ambientLight = new THREE.AmbientLight(0x404040, 2);
  scene.add(ambientLight);
  
  // Add point lights for each participant
  participants.forEach(participant => {
    const light = new THREE.PointLight(participant.color, 1, 10);
    const angle = angleStep * participant.position;
    const x = Math.cos(angle) * ringRadius;
    const z = Math.sin(angle) * ringRadius;
    light.position.set(x, 2, z);
    scene.add(light);
  });
  
  // Animation loop
  let time = 0;
  function animate() {
    requestAnimationFrame(animate);
    time += 0.01;
    
    // Rotate hologram group slowly
    hologramGroup.rotation.y += 0.002;
    
    // Animate individual participants
    participants.forEach((participant, index) => {
      if (participant.mesh) {
        // Pulse effect
        const scale = 1 + Math.sin(time * 2 + index) * 0.05;
        participant.mesh.platform.scale.y = scale;
        
        // Rotate rings
        participant.mesh.rings.forEach((ring, ringIndex) => {
          ring.rotation.z += 0.01 * (ringIndex + 1);
          ring.position.y = 1 + ringIndex * 0.5 + Math.sin(time * 3 + ringIndex) * 0.1;
        });
        
        // Beam pulsing
        participant.mesh.beam.material.opacity = 0.1 + Math.sin(time * 2 + index) * 0.1;
      }
    });
    
    renderer.render(scene, camera);
  }
  animate();
  
  // Handle window resize
  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });
  
  return { scene, camera, renderer, hologramGroup, participants };
}

// ================================
// CALL TIMER
// ================================
function initCallTimer() {
  const timerElement = document.querySelector('.call-timer');
  if (!timerElement) return;
  
  function updateTimer() {
    const elapsed = Date.now() - state.callStartTime;
    const hours = Math.floor(elapsed / 3600000);
    const minutes = Math.floor((elapsed % 3600000) / 60000);
    const seconds = Math.floor((elapsed % 60000) / 1000);
    
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    timerElement.querySelector('span:last-child').textContent = timeString;
  }
  
  updateTimer();
  state.timerInterval = setInterval(updateTimer, 1000);
}

// ================================
// FEATURE TOGGLES
// ================================
function initFeatureToggles() {
  const toggles = document.querySelectorAll('.feature-toggle input[type="checkbox"]');
  
  toggles.forEach(toggle => {
    const featureName = toggle.closest('.feature-item').querySelector('h4').textContent.toLowerCase().replace(' ', '');
    
    // Set initial state
    if (state.features[featureName] !== undefined) {
      toggle.checked = state.features[featureName];
    }
    
    toggle.addEventListener('change', (e) => {
      const isEnabled = e.target.checked;
      state.features[featureName] = isEnabled;
      
      // Animate the feature item
      const featureItem = e.target.closest('.feature-item');
      gsap.fromTo(featureItem,
        { scale: 1 },
        { scale: 1.05, duration: 0.2, yoyo: true, repeat: 1 }
      );
      
      console.log(`${featureName} ${isEnabled ? 'enabled' : 'disabled'}`);
      
      // Show notification
      showNotification(`${featureName.charAt(0).toUpperCase() + featureName.slice(1)} ${isEnabled ? 'enabled' : 'disabled'}`);
    });
  });
}

// ================================
// CONTROL BUTTONS
// ================================
function initControlButtons() {
  const controlButtons = document.querySelectorAll('.control-btn');
  
  controlButtons.forEach(btn => {
    btn.addEventListener('click', function() {
      const btnClass = this.className.split(' ').find(c => c !== 'control-btn');
      
      if (btnClass === 'leave') {
        handleLeaveCall();
        return;
      }
      
      // Toggle active state
      this.classList.toggle('active');
      this.classList.toggle('muted');
      
      // Update state
      if (state.controls.hasOwnProperty(btnClass)) {
        state.controls[btnClass] = !state.controls[btnClass];
      }
      
      // Animate button
      gsap.fromTo(this,
        { scale: 1 },
        { scale: 0.9, duration: 0.1, yoyo: true, repeat: 1 }
      );
      
      console.log(`${btnClass} toggled:`, state.controls[btnClass]);
      
      // Show notification
      const isActive = this.classList.contains('active');
      showNotification(`${btnClass.charAt(0).toUpperCase() + btnClass.slice(1)} ${isActive ? 'enabled' : 'disabled'}`);
    });
  });
}

// ================================
// PARTICIPANT INTERACTIONS
// ================================
function initParticipantInteractions() {
  const participantItems = document.querySelectorAll('.participant-item');
  
  participantItems.forEach(item => {
    item.addEventListener('click', function() {
      const participantName = this.querySelector('.participant-name').textContent;
      
      // Highlight active speaker
      const activeSpeaker = document.querySelector('.active-speaker');
      if (activeSpeaker) {
        const nameElement = activeSpeaker.querySelector('.speaker-info h3');
        const avatarElement = activeSpeaker.querySelector('.speaker-avatar');
        const participantAvatar = this.querySelector('.participant-avatar').textContent;
        
        if (nameElement) nameElement.textContent = participantName;
        if (avatarElement) avatarElement.textContent = participantAvatar;
        
        // Animate active speaker change
        gsap.fromTo(activeSpeaker,
          { scale: 1, opacity: 1 },
          { scale: 0.95, opacity: 0.8, duration: 0.2, yoyo: true, repeat: 1 }
        );
      }
      
      // Animate clicked participant
      gsap.fromTo(this,
        { scale: 1 },
        { scale: 1.1, duration: 0.2, yoyo: true, repeat: 1 }
      );
      
      console.log('Selected participant:', participantName);
    });
  });
}

// ================================
// LEAVE CALL
// ================================
function handleLeaveCall() {
  const confirmation = confirm('Are you sure you want to leave the call?');
  if (!confirmation) return;
  
  // Clear timer
  if (state.timerInterval) {
    clearInterval(state.timerInterval);
  }
  
  // Show leaving animation
  const body = document.body;
  gsap.to(body, {
    opacity: 0,
    duration: 0.5,
    onComplete: () => {
      window.location.href = '/';
    }
  });
}

// ================================
// NOTIFICATIONS
// ================================
function showNotification(message) {
  const notification = document.createElement('div');
  notification.style.cssText = `
    position: fixed;
    top: 100px;
    left: 50%;
    transform: translateX(-50%) translateY(-20px);
    background: rgba(0, 243, 255, 0.2);
    border: 1px solid rgba(0, 243, 255, 0.5);
    border-radius: 15px;
    padding: 15px 30px;
    color: white;
    font-family: 'Orbitron', sans-serif;
    backdrop-filter: blur(10px);
    z-index: 10000;
    opacity: 0;
    pointer-events: none;
  `;
  notification.textContent = message;
  document.body.appendChild(notification);
  
  // Animate in
  gsap.to(notification, {
    opacity: 1,
    y: 0,
    duration: 0.3
  });
  
  // Animate out and remove
  setTimeout(() => {
    gsap.to(notification, {
      opacity: 0,
      y: -20,
      duration: 0.3,
      onComplete: () => notification.remove()
    });
  }, 2000);
}

// ================================
// LOADING SCREEN
// ================================
function hideLoadingScreen() {
  const loadingScreen = document.getElementById('loading-screen');
  if (!loadingScreen) return;
  
  setTimeout(() => {
    gsap.to(loadingScreen, {
      opacity: 0,
      duration: 0.5,
      onComplete: () => {
        loadingScreen.classList.add('hidden');
      }
    });
  }, 2000);
}

// ================================
// STATS UPDATE
// ================================
function updateStats() {
  const latencyValue = document.querySelector('.stat-card:nth-child(1) .stat-value');
  const qualityValue = document.querySelector('.stat-card:nth-child(2) .stat-value');
  
  setInterval(() => {
    if (latencyValue) {
      const latency = Math.floor(Math.random() * 10) + 5;
      latencyValue.textContent = `${latency}ms`;
      latencyValue.className = 'stat-value';
      if (latency > 12) latencyValue.classList.add('warning');
      if (latency > 15) latencyValue.classList.add('error');
    }
    
    if (qualityValue) {
      const quality = Math.floor(Math.random() * 5) + 95;
      qualityValue.textContent = `${quality}%`;
    }
  }, 3000);
}

// ================================
// KEYBOARD SHORTCUTS
// ================================
function initKeyboardShortcuts() {
  document.addEventListener('keydown', (e) => {
    // M - Toggle mic
    if (e.key === 'm' || e.key === 'M') {
      const micBtn = document.querySelector('.control-btn:nth-child(1)');
      if (micBtn) micBtn.click();
    }
    
    // V - Toggle camera
    if (e.key === 'v' || e.key === 'V') {
      const camBtn = document.querySelector('.control-btn:nth-child(2)');
      if (camBtn) camBtn.click();
    }
    
    // H - Toggle hand
    if (e.key === 'h' || e.key === 'H') {
      const handBtn = document.querySelector('.control-btn:nth-child(3)');
      if (handBtn) handBtn.click();
    }
    
    // Esc - Leave call
    if (e.key === 'Escape') {
      handleLeaveCall();
    }
  });
}

// ================================
// INITIALIZATION
// ================================
function init() {
  console.log('🌐 Initializing Hologram Call Interface...');
  
  // Initialize all features
  initHologramScene();
  initCallTimer();
  initFeatureToggles();
  initControlButtons();
  initParticipantInteractions();
  initKeyboardShortcuts();
  updateStats();
  hideLoadingScreen();
  
  // Add entry animation
  gsap.fromTo('.holo-nav',
    { opacity: 0, y: -50 },
    { opacity: 1, y: 0, duration: 0.8, delay: 0.5 }
  );
  
  gsap.fromTo('.control-bar',
    { opacity: 0, y: 50 },
    { opacity: 1, y: 0, duration: 0.8, delay: 0.7 }
  );
  
  gsap.fromTo('.participants-panel',
    { opacity: 0, x: 50 },
    { opacity: 1, x: 0, duration: 0.8, delay: 0.9 }
  );
  
  gsap.fromTo('.features-panel',
    { opacity: 0, x: -50 },
    { opacity: 1, x: 0, duration: 0.8, delay: 1.1 }
  );
  
  console.log('✨ Hologram Call Interface initialized!');
}

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export state for potential use
export { state };
